import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../../core/shared/hooks/useApi.js';

// === Capaciteit ===

interface CapaciteitDag {
  datum: string;
  dagNaam: string;
  beschikbaar: boolean;
  beschikbareUren: number;
  geplandeUren: number;
  bezetting: number;
  notitie: string | null;
}

interface CapaciteitMedewerker {
  userId: string;
  naam: string;
  role: string;
  dagen: CapaciteitDag[];
}

interface CapaciteitData {
  week: number;
  jaar: number;
  medewerkers: CapaciteitMedewerker[];
}

export function useCapaciteit(week: number, jaar: number) {
  return useQuery({
    queryKey: ['planning', 'capaciteit', week, jaar],
    queryFn: () => apiFetch<CapaciteitData>(`/api/planning/capaciteit?week=${week}&jaar=${jaar}`),
  });
}

export function useUpdateBeschikbaarheid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ...data }: {
      userId: string;
      dagVanWeek: number;
      startTijd?: string;
      eindTijd?: string;
      beschikbaar?: boolean;
      datum?: string;
      notitie?: string;
    }) => apiFetch(`/api/planning/beschikbaarheid/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['planning', 'capaciteit'] }),
  });
}

// === Verlof ===

export function useMarkeerVerlof() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { userId: string; datum: string; notitie?: string }) =>
      apiFetch('/api/planning/verlof', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['planning', 'capaciteit'] }),
  });
}

export function useAnnuleerVerlof() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { userId: string; datum: string }) =>
      apiFetch('/api/planning/verlof', { method: 'DELETE', body: JSON.stringify(data) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['planning', 'capaciteit'] }),
  });
}

// === Dag Detail ===

interface DagDetailItem {
  id: string;
  source: 'afspraak' | 'stap';
  titel: string;
  type: string;
  icon: string;
  klant: string;
  dossierNr: string;
  dossierId: string;
  adres: string;
  vanUur: string;
  totUur: string;
}

interface DagDetailData {
  userId: string;
  datum: string;
  items: DagDetailItem[];
}

export function useDagDetail(userId: string | undefined, datum: string | undefined) {
  return useQuery({
    queryKey: ['planning', 'dag-detail', userId, datum],
    queryFn: () => apiFetch<DagDetailData>(`/api/planning/dag-detail?userId=${userId}&datum=${datum}`),
    enabled: !!userId && !!datum,
  });
}

// === Feestdagen ===

interface Feestdag {
  datum: string;
  naam: string;
  type: 'vast' | 'variabel';
}

export function useFeestdagen(jaar: number) {
  return useQuery({
    queryKey: ['planning', 'feestdagen', jaar],
    queryFn: () => apiFetch<Feestdag[]>(`/api/planning/feestdagen?jaar=${jaar}`),
    staleTime: 24 * 60 * 60 * 1000, // feestdagen change once a year
  });
}

// === Conflict Detectie ===

interface Conflict {
  type: string;
  beschrijving: string;
  ernst: 'blokkeer' | 'waarschuwing';
}

interface ConflictCheckResult {
  heeftConflicten: boolean;
  heeftBlokkerend: boolean;
  conflicten: Conflict[];
}

export function useCheckConflicten() {
  return useMutation({
    mutationFn: (data: {
      medewerkerId?: string;
      datum: string;
      vanUur: string;
      totUur: string;
      excludeAfspraakId?: string;
    }) => apiFetch<ConflictCheckResult>('/api/planning/conflicten', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  });
}

// === Auto-scheduling ===

interface Suggestie {
  datum: string;
  vanUur: string;
  totUur: string;
  score: number;
  reden: string;
}

interface SuggestieResult {
  suggesties: Suggestie[];
}

export function useAutoSuggestie() {
  return useMutation({
    mutationFn: (data: {
      medewerkerId?: string;
      duur?: number;
      vanafDatum?: string;
      type?: string;
      dossierId?: string;
    }) => apiFetch<SuggestieResult>('/api/planning/suggestie', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  });
}

// === Klant Bevestigingen ===

export function useBevestigAfspraak() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/planning/afspraken/${id}/bevestig`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['buitendienst', 'planning'] }),
  });
}

export function useAnnuleerAfspraak() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reden }: { id: string; reden?: string }) =>
      apiFetch(`/api/planning/afspraken/${id}/annuleer`, {
        method: 'POST',
        body: JSON.stringify({ reden }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['buitendienst', 'planning'] }),
  });
}

export function useVoltooiAfspraak() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/planning/afspraken/${id}/voltooi`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['buitendienst', 'planning'] }),
  });
}

// === Herinneringen ===

export function useVerstuurHerinnering() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (afspraakId: string) =>
      apiFetch<{ success: boolean; message: string }>(`/api/planning/afspraken/${afspraakId}/herinnering`, {
        method: 'POST',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['buitendienst', 'planning'] }),
  });
}

// === Dossier Afspraken ===

interface DossierAfspraak {
  id: string;
  titel: string;
  status: string;
  bevestigdeDatum: string | null;
  herinneringVerstuurd: boolean;
  aanmaker: { id: string; firstName: string; lastName: string };
  datums: { datum: string; vanUur: string | null; totUur: string | null }[];
  trajectstap: {
    template: { naam: string; icon: string };
  };
}

interface DossierStap {
  id: string;
  status: string;
  geplandeDatum: string | null;
  template: { naam: string; icon: string };
}

interface DossierAfsprakenData {
  afspraken: DossierAfspraak[];
  stappen: DossierStap[];
}

export function useDossierAfspraken(dossierId: string | undefined) {
  return useQuery({
    queryKey: ['planning', 'dossier-afspraken', dossierId],
    queryFn: () => apiFetch<DossierAfsprakenData>(`/api/planning/afspraken/dossier/${dossierId}`),
    enabled: !!dossierId,
  });
}

// === Export ===

interface ExportItem {
  tijd: string;
  type: string;
  klant: string;
  dossierNr: string;
  adres: string;
}

interface ExportDag {
  datum: string;
  dagNaam: string;
  items: ExportItem[];
}

interface ExportMedewerker {
  medewerker: string;
  role: string;
  dagen: ExportDag[];
}

export interface WeekExportData {
  week: number;
  jaar: number;
  data: ExportMedewerker[];
}

export function useWeekExport(week: number, jaar: number, enabled: boolean) {
  return useQuery({
    queryKey: ['planning', 'export', week, jaar],
    queryFn: () => apiFetch<WeekExportData>(`/api/planning/export/week?week=${week}&jaar=${jaar}&medewerker=all`),
    enabled,
  });
}

// === Uitgebreid Auto-Scheduling ===

interface AutoScheduleSuggestie {
  score: number;
  datum: string;
  startTijd: string;
  eindTijd: string;
  resources: { id: string; naam: string; type: string }[];
  reden: string;
}

export function useAutoSchedule() {
  return useMutation({
    mutationFn: (data: {
      type?: string;
      dossierId?: string;
      duurMinuten?: number;
      prioriteit?: string;
      voorkeursDagen?: string[];
      voorkeursOchtend?: boolean;
      deadline?: string;
    }) => apiFetch<{ suggesties: AutoScheduleSuggestie[] }>('/api/planning/auto-schedule', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  });
}

export interface OngeplandItem {
  /** trajectstap-id (achterwaarts compatibel) — niet uniek als een stap ook een te plannen DNV heeft: gebruik `sleutel` */
  id: string;
  sleutel?: string;
  /** 'stap' = actieve trajectstap zonder datum · 'dnv' = te plannen DNV/Nazorg-afspraak (bv. uit een DNV-mail) */
  soort?: 'stap' | 'dnv';
  trajectstapId?: string;
  /** enkel bij soort 'dnv': inplannen = POST /api/buitendienst/planning/afspraken met dit afspraakId (of PATCH met datum) */
  afspraakId?: string | null;
  type: string;
  typeCode?: 'opmeting' | 'plaatsing' | 'nazorg' | 'overig';
  icon: string;
  titel?: string;
  klant: string;
  dossierNr: string;
  dossierId: string;
  adres: string;
  postcode?: string | null;
  stad?: string | null;
  lat?: number | null;
  lng?: number | null;
  winkel?: { id: string; naam: string } | null;
  geschatteDuurMinuten?: number | null;
  /** YYYY-MM-DD: vroegst planbare dag (maandag van de leverweek) */
  vroegsteDatum?: string | null;
  leverweek?: number | null;
  notitie?: string | null;
  aangevraagdOp?: string | null;
}

export function useOngepland() {
  return useQuery({
    queryKey: ['planning', 'ongepland'],
    queryFn: () => apiFetch<{ items: OngeplandItem[]; totaal: number }>('/api/planning/ongepland'),
  });
}

export function useAccepteerSuggesties() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (toewijzingen: { trajectstapId: string; medewerkerId: string; datum: string; startTijd: string; eindTijd: string }[]) =>
      apiFetch<{ resultaten: any[]; gepland: number }>('/api/planning/auto-schedule/accepteer', {
        method: 'POST',
        body: JSON.stringify({ toewijzingen }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['planning'] });
      qc.invalidateQueries({ queryKey: ['buitendienst', 'planning'] });
    },
  });
}

// === Bevestigingen ===

export function useVerstuurBevestiging() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (afspraakId: string) =>
      apiFetch<{ bevestiging: any; hash: string }>(`/api/planning/bevestigingen/verstuur/${afspraakId}`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['planning', 'bevestigingen'] }),
  });
}

export function useBatchHerinneringen() {
  return useMutation({
    mutationFn: () =>
      apiFetch<{ verstuurd: number; totaal: number }>('/api/planning/bevestigingen/herinnering', { method: 'POST' }),
  });
}

interface PlanningBevestigingItem {
  id: string;
  klantNaam: string;
  type: string;
  kanaal: string;
  status: string;
  hash: string;
  verzondenOp: string;
  bevestigdOp: string | null;
}

export function useBevestigingen(status?: string) {
  const params = status ? `?status=${status}` : '';
  return useQuery({
    queryKey: ['planning', 'bevestigingen', status],
    queryFn: () => apiFetch<PlanningBevestigingItem[]>(`/api/planning/bevestigingen${params}`),
  });
}

// === Audit Log ===

interface AuditLogItem {
  id: string;
  actie: string;
  vanWaarde: any;
  naarWaarde: any;
  userId: string | null;
  reden: string | null;
  createdAt: string;
}

export function useAuditLog(afspraakId: string | undefined) {
  return useQuery({
    queryKey: ['planning', 'audit', afspraakId],
    queryFn: () => apiFetch<AuditLogItem[]>(`/api/planning/audit/${afspraakId}`),
    enabled: !!afspraakId,
  });
}

// === Periode Conflicten ===

export function usePeriodeConflicten(van: string, tot: string, enabled: boolean) {
  return useQuery({
    queryKey: ['planning', 'conflicten-periode', van, tot],
    queryFn: () => apiFetch<{ conflicten: any[]; totaal: number }>(`/api/planning/conflicten/periode?van=${van}&tot=${tot}`),
    enabled,
  });
}

// === R6: Multi-resource scheduling ===

export function useResources() {
  return useQuery({
    queryKey: ['planning', 'resources'],
    queryFn: () => apiFetch<{ resources: any[] }>('/api/planning/resources'),
  });
}

export function useCreateResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { naam: string; type: string; beschikbaarheid?: string }) =>
      apiFetch('/api/planning/resources', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['planning', 'resources'] }),
  });
}

export function useAfspraakTeam(afspraakId: string | undefined) {
  return useQuery({
    queryKey: ['planning', 'afspraak-team', afspraakId],
    queryFn: () => apiFetch<any>(`/api/planning/afspraken/${afspraakId}/team`),
    enabled: !!afspraakId,
  });
}

export function useTeamSuggesties(startTijdstip: string, eindTijdstip: string, aantalNodig: number, enabled: boolean) {
  return useQuery({
    queryKey: ['planning', 'team-suggesties', startTijdstip, eindTijdstip, aantalNodig],
    queryFn: () => apiFetch<{ suggesties: any[] }>(`/api/planning/suggesties/medewerkers?startTijdstip=${startTijdstip}&eindTijdstip=${eindTijdstip}&aantalNodig=${aantalNodig}`),
    enabled,
  });
}

// === R6: Seizoensplanning ===

export function useSeizoenPeriodes() {
  return useQuery({
    queryKey: ['planning', 'seizoenen'],
    queryFn: () => apiFetch<{ seizoenen: any[] }>('/api/planning/seizoenen'),
  });
}

export function useSeizoenForecast(seizoenId: string | undefined) {
  return useQuery({
    queryKey: ['planning', 'seizoen-forecast', seizoenId],
    queryFn: () => apiFetch<{ forecast: any[] }>(`/api/planning/seizoenen/${seizoenId}/forecast`),
    enabled: !!seizoenId,
  });
}

export function useKomende90DagenForecast() {
  return useQuery({
    queryKey: ['planning', 'forecast-90d'],
    queryFn: () => apiFetch<{ periodes: any[] }>('/api/planning/forecast/komende-90-dagen'),
  });
}

// === R6: Conflict Detector ===

export function usePlanningConflicten(status?: string) {
  return useQuery({
    queryKey: ['planning', 'conflict-detector', status],
    queryFn: () => apiFetch<{ conflicten: any[] }>(`/api/planning/conflicten${status ? `?status=${status}` : ''}`),
  });
}

export function useCheckAfspraakConflicts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (afspraakId: string) =>
      apiFetch<{ hasConflicts: boolean; conflicten: any[] }>(`/api/planning/afspraken/${afspraakId}/check-conflicts`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['planning', 'conflict-detector'] }),
  });
}

// === R6: Export ===

export function useIcalFeedUrl() {
  return useQuery({
    queryKey: ['planning', 'ical-url'],
    queryFn: () => apiFetch<{ url: string }>('/api/planning/export/ical-url'),
  });
}
